import axios from 'axios';

/* ================================================================
   Auth service — single login page, multi-user, multi-carrier

   POST /api/auth/login authenticates everyone. The UserType it
   returns decides which of the three dashboards the user lands on:

     "NowCerts"    -> NowCerts dashboard (the existing MTM screens)
     "Carrier"     -> Carrier dashboard
     "SubCarrier"  -> SubCarrier user dashboard

   readValue() tolerates casing differences and decodeJwt() falls back
   to the token's own claims when a field is absent from the body.
   ================================================================ */

/**
 * Base URL for /api/auth/login.
 *
 * AuthController sits in the same project as AccountController, so the login
 * endpoint is served by the trucking API host — the same variable the rest of
 * the portal already uses. No separate URL is needed.
 */
export const AUTH_BASE_URL = (process.env.REACT_APP_TRUCKING_API_Url || '')
  .trim()
  .replace(/\/+$/, '');

export type UserRole =
  | 'NOWCERTS_ADMIN'
  | 'NOWCERTS_USER'
  | 'CARRIER_ADMIN'
  | 'CARRIER_MANAGER'
  | 'CARRIER_AGENT'
  | 'CARRIER_CSR';

export type ApiUserType = 'NowCerts' | 'Carrier' | 'SubCarrier';

export interface AuthUser {
  userId: string;
  userName: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  avatarName: string;
  isCarrierUser: boolean;
  carrierId: number | null;
  carrierName: string;
  roleName: UserRole;
  token: string;
  /** Raw UserType from the API — the value routing is actually based on. */
  userType: ApiUserType;
  /** NowCerts access token; empty for carrier users, who have no AMS account. */
  ncToken: string;
  mustChangePassword: boolean;
  nowCertsAgentId: string;
  nowCertsAgencyId: string;
}

const STORAGE_KEY = 'mtmAuthUser';

function readValue(source: any, keys: string[]): any {
  if (!source) return undefined;
  for (const key of keys) {
      if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
      return source[key];
    }
    const match = Object.keys(source).find(
      k => k.toLowerCase() === key.toLowerCase()
    );
    if (match && source[match] !== undefined && source[match] !== null && source[match] !== '') {
      return source[match];
    }
  }
  return undefined;
}

export function decodeJwt(token: string): Record<string, any> {
  try {
    const payload = token.split('.')[1];
    if (!payload) return {};
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '==='.slice((base64.length + 3) % 4);
    return JSON.parse(decodeURIComponent(escape(window.atob(padded))));
  } catch {
    return {};
  }
}

function toBool(value: any): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    return ['1', 'true', 'yes', 'y'].includes(value.trim().toLowerCase());
  }
  return false;
}

/**
 * Read the API's UserType. It arrives as the enum name ("NowCerts", "Carrier",
 * "SubCarrier"); the numeric form is accepted too in case a caller sends the
 * raw byte (1 / 2 / 3).
 */
function readUserType(raw: any): ApiUserType {
  const value = String(raw ?? '').trim().toLowerCase();
  if (value === 'carrier' || value === '2') return 'Carrier';
  if (value === 'subcarrier' || value === 'sub_carrier' || value === '3') return 'SubCarrier';
  return 'NowCerts';
}

function roleFromUserType(userType: ApiUserType, isAdmin: boolean): UserRole {
  if (userType === 'Carrier') return 'CARRIER_ADMIN';
  if (userType === 'SubCarrier') return 'CARRIER_AGENT';
  return isAdmin ? 'NOWCERTS_ADMIN' : 'NOWCERTS_USER';
}

function initials(first: string, last: string, fallback: string): string {
  const a = (first || '').trim().charAt(0);
  const b = (last || '').trim().charAt(0);
  if (a || b) return `${a}${b}`.toUpperCase();
  return (fallback || 'U').trim().slice(0, 2).toUpperCase();
}

export function buildAuthUser(raw: any): AuthUser {
  const body = raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
    ? { ...raw, ...raw.data }
    : raw || {};

  const token = String(
    readValue(body, ['token', 'accessToken', 'access_token', 'jwt', 'idToken']) || ''
  );
  const claims = token ? decodeJwt(token) : {};
  const merged = { ...claims, ...body };

  const email = String(
    readValue(merged, ['email', 'workemail', 'workEmail', 'userName', 'username']) || ''
  );
  const userName = String(readValue(merged, ['userName', 'username', 'name']) || email);
  const firstName = String(readValue(merged, ['firstName', 'givenName']) || '');
  const lastName = String(readValue(merged, ['lastName', 'surname', 'familyName']) || '');

  const userType = readUserType(readValue(merged, ['userType', 'UserType']));
  const isCarrierUser = userType !== 'NowCerts';
  const isAdmin = toBool(readValue(merged, ['isAdminUser', 'IsAdminUser']));
  const roleName = roleFromUserType(userType, isAdmin);

  const userId = String(readValue(merged, ['userId', 'UserId', 'id', 'idUser']) || '');

  /*
   * The login response has no CarrierId. A Carrier user IS the carrier, so their
   * own UserId identifies it; a SubCarrier hangs off ParentUserId, which the API
   * does not expose yet — so it stays null until the endpoint returns it, and
   * carrier-scoped reads must rely on the server filtering by the token, not this.
   */
  const rawCarrierId = readValue(merged, ['carrierId', 'CarrierId', 'parentUserId', 'ParentUserId']);
  let carrierId: number | null =
    rawCarrierId === undefined || rawCarrierId === null || rawCarrierId === ''
      ? null
      : Number(rawCarrierId);
  if (carrierId === null && userType === 'Carrier' && userId) {
    carrierId = Number(userId);
  }

  const fullName =
    [firstName, lastName].filter(Boolean).join(' ') ||
    userName.split('@')[0].split(/[._-]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return {
    userId,
    userName,
    email,
    firstName,
    lastName,
    fullName,
    avatarName: String(
      readValue(merged, ['avatarName', 'AvatarName']) || initials(firstName, lastName, fullName)
    ),
    isCarrierUser,
    carrierId: Number.isNaN(carrierId as number) ? null : carrierId,
    carrierName: String(
      readValue(merged, ['carrierName', 'CarrierName', 'companyName', 'agencyName']) || ''
    ),
    roleName,
    token,
    userType,
    ncToken: String(readValue(merged, ['ncToken', 'NCToken']) || ''),
    mustChangePassword: toBool(readValue(merged, ['mustChangePassword', 'MustChangePassword'])),
    nowCertsAgentId: String(readValue(merged, ['nowCertsAgentId', 'NowCertsAgentId']) || ''),
    nowCertsAgencyId: String(readValue(merged, ['nowCertsAgencyId', 'NowCertsAgencyId']) || ''),
  };
}

export async function login(username: string, password: string): Promise<AuthUser> {
  if (!AUTH_BASE_URL) {
    throw new Error(
      'Login API URL is not configured. Set REACT_APP_TRUCKING_API_Url in your .env and restart.'
    );
  }

  const response = await axios.post(
    `${AUTH_BASE_URL}/api/auth/login`,
    { username, password },
    { headers: { 'Content-Type': 'application/json' } }
  );

  const user = buildAuthUser(response.data);
  if (!user.token) {
    throw new Error('Login succeeded but no token was returned.');
  }

  persistAuthUser(user);
  return user;
}

export function persistAuthUser(user: AuthUser): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(user));

  // Keys the existing pages already depend on — keep them in sync.
  // NCToken drives every AMS call (X-NC-Token); it is empty for carrier users,
  // who have no NowCerts account, so those AMS features simply won't apply.
  localStorage.setItem('igtoken', user.token);
  localStorage.setItem('userName', user.userName);
  localStorage.setItem('AvatarName', user.avatarName);
  localStorage.setItem('name', user.fullName);
  localStorage.setItem('agentEmail', user.email);
  localStorage.setItem('NCToken', user.ncToken);
  localStorage.setItem('NowcertsAgentId', user.nowCertsAgentId);
  localStorage.setItem('NCAgencyId', user.nowCertsAgencyId);
  localStorage.setItem('IsAdminUser', String(user.roleName.endsWith('_ADMIN')));

  sessionStorage.setItem('isAuthenticated', 'true');
  sessionStorage.setItem('userEmail', user.email);
  sessionStorage.setItem('authMethod', user.isCarrierUser ? 'carrier' : 'nowcerts');
}

export function getAuthUser(): AuthUser | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as AuthUser;
  } catch {
  }

  // Session created by the older NowCerts SSO flow (Validation.tsx)
  const token = localStorage.getItem('igtoken');
  if (!token) return null;
  return buildAuthUser({
    token,
    userName: localStorage.getItem('userName') || '',
    email: localStorage.getItem('agentEmail') || '',
    userType: 'NowCerts',
    isAdminUser: localStorage.getItem('IsAdminUser') === 'true',
    ncToken: localStorage.getItem('NCToken') || '',
    nowCertsAgentId: localStorage.getItem('NowcertsAgentId') || '',
    nowCertsAgencyId: localStorage.getItem('NCAgencyId') || '',
  });
}

export function logout(): void {
  localStorage.clear();
  sessionStorage.clear();
}

export const isNowCertsUser = (u: AuthUser | null) => !!u && !u.isCarrierUser;

export const isCarrierAdmin = (u: AuthUser | null) =>
  !!u && u.isCarrierUser && (u.roleName === 'CARRIER_ADMIN' || u.roleName === 'CARRIER_MANAGER');

export const isCarrierSubUser = (u: AuthUser | null) =>
  !!u && u.isCarrierUser && (u.roleName === 'CARRIER_AGENT' || u.roleName === 'CARRIER_CSR');

/** Only a carrier admin may add, edit or remove users on their carrier. */
export const canManageCarrierUsers = (u: AuthUser | null) =>
  !!u && u.roleName === 'CARRIER_ADMIN';

export const roleLabel = (role: UserRole): string => ({
  NOWCERTS_ADMIN: 'NowCerts Admin',
  NOWCERTS_USER: 'NowCerts User',
  CARRIER_ADMIN: 'Carrier Admin',
  CARRIER_MANAGER: 'Carrier Manager',
  CARRIER_AGENT: 'Carrier Agent',
  CARRIER_CSR: 'Carrier CSR',
}[role] || role);

/**
 * Where this user lands after login, and what "/" redirects to.
 *
 * NowCerts users keep the original destination — /quote-history — so the
 * existing MTM flow is unchanged. Only the carrier roles are new.
 */
export function homeRouteFor(user: AuthUser | null): string {
  if (!user) return '/login';
  if (isCarrierSubUser(user)) return '/my-quotes';
  if (isCarrierAdmin(user)) return '/carrier';
  return '/quote-history';
}
